import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Loader2, TrendingUp, TrendingDown } from "lucide-react";
import { api, money, errorMessage } from "../api";
import { Preview, ddmmyyyy } from "./Digest";

const hourLabel = (h) => `${((h + 11) % 12) + 1} ${h < 12 ? "AM" : "PM"}`;
const lastMonday = () => {
  const d = new Date(Date.now() + 330 * 60000);
  const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day - 7);
  return d.toISOString().slice(0, 10);
};
const pct = (p) => (p == null ? "n/a" : `${p >= 0 ? "+" : ""}${p}%`);
const dd = (iso) => iso.split("-").reverse().slice(0, 2).join("-");

export function weeklyText(r, origin) {
  const m = r.metrics;
  const day = (x) => (x ? `${x.weekday} ${dd(x.date)} – ${money(x.sales)}` : "—");
  return [
    `*FynDine Weekly Report*`,
    `${r.branch_name}${r.branch_city ? ", " + r.branch_city : ""} · ${dd(r.week_start)} to ${ddmmyyyy(r.week_end)}`,
    ``,
    `Sales: ${money(m.this.sales)} (${pct(m.change.sales_pct)} vs previous week)`,
    `Bills: ${m.this.bills} (${pct(m.change.bills_pct)}) · Avg bill ${money(m.this.avg_bill)} (${pct(m.change.avg_bill_pct)})`,
    `Best day: ${day(m.best_day)}`,
    `Worst day: ${day(m.worst_day)}`,
    `Top waiter: ${m.top_waiter ? `${m.top_waiter.name} – ${money(m.top_waiter.sales)}` : "—"}`,
    `Best-seller: ${m.best_seller ? `${m.best_seller.name} (${m.best_seller.qty} sold)` : "—"}`,
    `Peak hour: ${m.peak_hour ? hourLabel(m.peak_hour.hour) : "—"}`,
    ``,
    `Full report: ${origin}/weekly/${r.id}`,
  ].join("\n");
}

export function WeekCompare({ r }) {
  const m = r.metrics;
  const max = Math.max(1, ...m.days.map((d) => d.sales));
  const Delta = ({ p }) => (p == null ? <small>n/a</small> : <small className={p >= 0 ? "up" : "down"}>{p >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />} {pct(p)}</small>);
  return (
    <div className="wk-compare" data-testid="weekly-compare">
      <div className="wk-kpis">
        <div><span>Sales</span><b data-testid="weekly-sales">{money(m.this.sales)}</b><Delta p={m.change.sales_pct} /><em>prev {money(m.prev.sales)}</em></div>
        <div><span>Bills</span><b>{m.this.bills}</b><Delta p={m.change.bills_pct} /><em>prev {m.prev.bills}</em></div>
        <div><span>Avg bill</span><b>{money(m.this.avg_bill)}</b><Delta p={m.change.avg_bill_pct} /><em>prev {money(m.prev.avg_bill)}</em></div>
      </div>
      <div className="wk-days">
        {m.days.map((d) => (
          <div key={d.date} className={m.best_day?.date === d.date ? "best" : m.worst_day?.date === d.date ? "worst" : ""} data-testid={`weekly-day-${d.date}`}>
            <i style={{ height: `${(d.sales / max) * 100}%` }} /><b>{d.weekday}</b><small>{money(d.sales).replace(".00", "")}</small>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function WeeklyView({ branch }) {
  const [list, setList] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [sel, setSel] = useState(null);
  const [week, setWeek] = useState(lastMonday());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    try {
      const [l, c] = await Promise.all([api.get(`/weekly-reports?branch=${branch}`), api.get("/digest/settings")]);
      setList(l.data); setCfg(c.data); setSel((s) => l.data.find((x) => x.id === s?.id) || l.data[0] || null); setErr("");
    } catch (e) { setErr(errorMessage(e)); }
  }, [branch]);
  useEffect(() => { load(); }, [load]);
  const build = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/weekly-reports/generate?branch=${branch}&week_start=${week}`); await load(); setSel(data); }
    catch (e) { setErr(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <div className="dg-layout" data-testid="weekly-page">
      <div className="panel dg-list" data-testid="weekly-list">
        <div className="panel-head"><div><span className="overline">EVERY MONDAY · MON–SUN VS WEEK BEFORE</span><h2>Weekly reports</h2></div><button className="icon-button" onClick={load} data-testid="weekly-refresh"><RefreshCw size={14} /></button></div>
        <div className="dg-gen">
          <input type="date" lang="en-GB" value={week} onChange={(e) => setWeek(e.target.value)} data-testid="weekly-week-start" title="Pick the Monday the week starts on" />
          <button className="secondary-button" onClick={build} disabled={busy || !week} data-testid="weekly-generate-button">{busy ? <Loader2 className="spin" size={13} /> : <RefreshCw size={13} />} Build week</button>
        </div>
        {err && <div className="auth-alert error" data-testid="weekly-error">{err}</div>}
        {!list.length && <div className="empty small">No weekly reports yet. The first is prepared next Monday.</div>}
        {list.map((r) => (
          <button key={r.id} className={`dg-row ${sel?.id === r.id ? "on" : ""}`} onClick={() => setSel(r)} data-testid={`weekly-row-${r.week_start}`}>
            <b>{dd(r.week_start)}→{dd(r.week_end)}</b><span>{money(r.metrics.this.sales)} · {pct(r.metrics.change.sales_pct)}</span>
            <em className={r.sent_log?.length || r.deliveries?.length ? "pill success" : "pill accent"}>{r.deliveries?.length || r.sent_log?.length ? "sent" : "ready"}</em>
          </button>
        ))}
      </div>
      <div>
        {sel ? (
          <>
            <div className="panel" style={{ marginBottom: 16 }}><WeekCompare r={sel} /></div>
            <Preview d={sel} kind="weekly" title={`WEEK · ${dd(sel.week_start)} → ${ddmmyyyy(sel.week_end)}`} text={weeklyText(sel, window.location.origin)} recipients={cfg?.recipients || []} onSent={load} />
          </>
        ) : <div className="panel empty">Select or build a weekly report.</div>}
      </div>
    </div>
  );
}
